import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Toaster } from '@utilities-one/gpm-ui';
import { type LoginUserInput, LoginUserSchema } from '@utilities-one/reipm-api-sdk';

import bgPopup from '@/assets/img/home/BgPopup.png';
import fulfilmentLogo from '@/assets/img/home/fulfilmentLogo.svg';
import gpmLogo from '@/assets/img/home/gpmLogo.svg';
import type { MarketOption } from '@/entities/user/components/MarketSelect';
import { MarketSelectDialog } from '@/entities/user/components/MarketSelectDialog';
import { enterApp } from '@/entities/user/utils/enterApp';
import { canSwitchMarkets } from '@/entities/user/utils/multiMarket';
import { extractApiError } from '@/library/api/extractApiError';
import { api, parseResponse } from '@/library/api/honoClient';
import { useAppForm } from '@/library/form/hooks';

/*
 * Legacy-parity styling for the gpm-ui controls: the old NextUI inputs were a
 * 48px white surface, 1px `#232C37` border, 12px radius, with the field name
 * rendered INSIDE the control (NextUI inside-label ≈ placeholder here) at
 * every breakpoint — undo gpm-ui's `md:h-8 md:bg-transparent` compaction.
 * Border colors are set via inline style because gpm-ui's compiled style.css
 * ships an UNLAYERED `* { border-color: var(--border) }` reset that outranks
 * every (layered) Tailwind border-color utility — see globals.css.
 */
const inputSurfaceClassName =
  'h-12 rounded-[12px] bg-white px-3 text-[14px] font-normal text-[#232C37] placeholder:text-[#71717A] md:h-12 md:bg-white md:text-[14px]';
const inputBorderStyle = { borderColor: '#232C37' };

/**
 * Email/password login, styled as the legacy reipm login hero (verified
 * against https://reipm.utilitiesone.com/): full-bleed `BgPopup` map
 * background, 3px-blue-bordered white card with the GPM → "For" →
 * U1 Fulfillment logo row, blue "Log In" heading, welcome copy, in-field
 * labels and the "Developed by US Software Solutions" credit.
 *
 * Internals are the modern stack (unlike the legacy NextUI + server-action
 * version): TanStack Form (`useAppForm` + form-kit `TextInput` /
 * `PasswordInput` with the legacy eye toggle), the SDK's `LoginUserSchema`
 * for client-side validation, and a TanStack `useMutation` around
 * `POST /users/login`. The API verifies bcrypt credentials, creates a Session
 * row and sets the httpOnly `accessToken` cookie; on success we do a FULL
 * reload of `/` so the freshly-set cookie is applied to the boot request and
 * AccessProvider re-resolves `/users/me` — except multi-market users
 * (UG-4847), whose login response carries their markets and who pick the one
 * to enter in {@link MarketSelectDialog} first. API errors land under the
 * Email field via the form's `onServer` error map (cleared on the next
 * submit, which the DOM handler does explicitly — nothing clears `onServer`
 * automatically and stale entries block `handleSubmit`); the `Toaster`
 * remains for the success toast and MarketSelectDialog errors.
 *
 * The card shell is plain markup rather than gpm-ui `Card` on purpose — the
 * Card chrome (ring, elevation, muted bordered footer) fights the legacy
 * design; only the CONTROLS are required to be the new gpm-ui ones.
 */
export const LoginForm = () => {
  const [markets, setMarkets] = useState<MarketOption[] | null>(null);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginUserInput) => parseResponse(api.users.login.$post({ json: credentials })),
    onSuccess: (result) => {
      if (canSwitchMarkets(result)) {
        setMarkets(result.availableMarkets);
        return;
      }
      enterApp();
    },
  });

  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    validators: { onSubmit: LoginUserSchema },
    onSubmit: ({ value, formApi }) =>
      loginMutation.mutate(value, {
        onError: (error) => {
          formApi.setFieldMeta('email', (prev) => ({
            ...prev,
            errorMap: { ...prev.errorMap, onServer: { message: extractApiError(error, 'Login failed') } },
            errorSourceMap: { ...prev.errorSourceMap, onServer: 'field' },
          }));
        },
      }),
  });

  const isPending = loginMutation.isPending || loginMutation.isSuccess;

  return (
    <div className="relative min-h-[740px] w-full overflow-hidden px-3 pt-8 md:min-h-[676px] md:pt-2">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="absolute inset-0">
        <img src={bgPopup} alt="" className="h-full w-full object-cover object-center" />
      </div>
      <form
        className="relative mx-auto max-w-[555px]"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.setFieldMeta('email', (prev) => ({
            ...prev,
            errorMap: { ...prev.errorMap, onServer: undefined },
            errorSourceMap: { ...prev.errorSourceMap, onServer: undefined },
          }));
          void form.handleSubmit();
        }}
      >
        <div
          className="mx-auto mt-8 max-w-full rounded-[14px] border-[3px] bg-white md:min-w-[570px]"
          style={{ borderColor: '#0061E5' }}
        >
          <div className="mt-10 flex items-center justify-center space-x-3">
            <img src={gpmLogo} alt="GPM Logo" width={64} height={64} />
            <p className="font-medium text-[#5D6268]">For</p>
            <a target="_blank" rel="noreferrer" href="https://utilitiesone.com/wireline-construction">
              <img src={fulfilmentLogo} alt="U1 Fulfillment Logo" width={114} height={40} />
            </a>
          </div>
          <div className="flex flex-col items-center justify-center p-3 pt-4">
            <p className="pb-1 text-center text-[32px] font-extrabold text-[#0061E5]">Log In</p>
            <p className="max-w-[353px] text-center text-[12px] font-medium text-[#101520] opacity-70">
              {`Welcome back to GPM! Access your projects, track progress, and make updates effortlessly—let's build success together.`}
            </p>
          </div>
          <form.AppForm>
            <div className="mx-auto mt-[25px] flex w-full max-w-full flex-col gap-6 px-3 md:max-w-[515px]">
              <form.AppField name="email">
                {(field) => (
                  <field.TextInput
                    label="Email"
                    showLabel={false}
                    inputProps={{
                      type: 'email',
                      autoComplete: 'email',
                      placeholder: 'Email',
                      'aria-label': 'Email',
                      className: inputSurfaceClassName,
                      style: inputBorderStyle,
                    }}
                  />
                )}
              </form.AppField>
              <form.AppField name="password">
                {(field) => (
                  <field.PasswordInput
                    label="Password"
                    showLabel={false}
                    groupProps={{
                      className: 'h-12 rounded-[12px] bg-white md:h-12 md:bg-white',
                      style: inputBorderStyle,
                    }}
                    inputProps={{
                      autoComplete: 'current-password',
                      placeholder: 'Password',
                      'aria-label': 'Password',
                      className: 'px-3 text-[14px] font-normal text-[#232C37] placeholder:text-[#71717A]',
                    }}
                  />
                )}
              </form.AppField>
            </div>
            <div className="mt-[33px] flex px-3 pb-[40px]">
              <form.SubmitButton
                label="Log In"
                loading={isPending}
                buttonProps={{
                  className:
                    'm-auto min-h-12 w-full rounded-[12px] bg-[#0061E5] text-[16px] font-bold text-white hover:bg-[#0061E5]/90 md:w-[491px]',
                }}
              />
            </div>
          </form.AppForm>
          <p className="pb-[32px] text-center text-[14px] font-medium lg:pb-[50px]">
            Developed by{' '}
            <a className="underline" href="https://ussoftwaresolutions.us/" target="_blank" rel="noreferrer">
              US Software Solutions
            </a>
          </p>
        </div>
      </form>
      {markets && <MarketSelectDialog markets={markets} />}
    </div>
  );
};
